import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, UserPlus, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import SashaAvatar from "../components/SashaAvatar";

/**
 * Dedicated, unlinked sign-on page for guests — offers both sign-in and
 * a quick create-account side by side so travellers don't have to hop
 * between routes.
 */
export default function GuestSignInPage() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/portal";

  const [signIn, setSignIn] = useState({ email: "", password: "" });
  const [register, setRegister] = useState({ name: "", email: "", password: "" });
  const [busyIn, setBusyIn] = useState(false);
  const [busyReg, setBusyReg] = useState(false);

  const submitSignIn = async (e) => {
    e.preventDefault();
    setBusyIn(true);
    try {
      await login(signIn.email, signIn.password);
      toast.success("Welcome back.");
      navigate(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed.");
    } finally {
      setBusyIn(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setBusyReg(true);
    try {
      const r = await api.post("/auth/register", { ...register, role: "client" });
      try {
        window.localStorage.setItem("sasha_token", r.data.token);
      } catch (_) {
        /* ignore */
      }
      toast.success(
        r.data?.user?.membership_number
          ? `Welcome. Your member number: ${r.data.user.membership_number}`
          : "Welcome to Janté."
      );
      await refresh();
      navigate(next);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account.");
    } finally {
      setBusyReg(false);
    }
  };

  return (
    <div
      className="max-w-6xl mx-auto px-6 lg:px-12 py-16 grid lg:grid-cols-[0.9fr_1fr] gap-12 items-start"
      data-testid="guest-signin-page"
    >
      <div className="hidden lg:flex flex-col items-center">
        <SashaAvatar size="lg" />
        <p className="mt-8 text-center text-[var(--ink-2)] max-w-sm" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.25rem" }}>
          A quiet door reserved for our guests — please sign in, or open your complimentary
          account to begin.
        </p>
      </div>

      <div>
        <div className="overline text-[var(--brand-red)]">Guests</div>
        <h1 className="font-display text-5xl text-[var(--emerald)] mt-3">Guest sign-in.</h1>

        <div className="grid sm:grid-cols-2 gap-6 mt-10">
          {/* Sign in */}
          <form onSubmit={submitSignIn} className="lux-card" data-testid="guest-signin-form">
            <div className="flex items-center gap-2">
              <LogIn size={16} strokeWidth={1.25} className="text-[var(--gold)]" />
              <div className="overline">Sign in</div>
            </div>
            <h2 className="font-display text-2xl text-[var(--emerald)] mt-3">Welcome back</h2>
            <div className="mt-5 space-y-4">
              <div>
                <div className="overline mb-1">Email</div>
                <input
                  className="lux-input"
                  type="email"
                  required
                  value={signIn.email}
                  onChange={(e) => setSignIn({ ...signIn, email: e.target.value })}
                  data-testid="guest-signin-email"
                />
              </div>
              <div>
                <div className="overline mb-1">Password</div>
                <input
                  className="lux-input"
                  type="password"
                  required
                  value={signIn.password}
                  onChange={(e) => setSignIn({ ...signIn, password: e.target.value })}
                  data-testid="guest-signin-password"
                />
              </div>
              <button
                type="submit"
                disabled={busyIn}
                className="btn-primary w-full"
                data-testid="guest-signin-submit"
              >
                {busyIn ? "Signing in…" : "Sign in"}
              </button>
            </div>
          </form>

          {/* Register */}
          <form onSubmit={submitRegister} className="lux-card" data-testid="guest-register-form">
            <div className="flex items-center gap-2">
              <UserPlus size={16} strokeWidth={1.25} className="text-[var(--gold)]" />
              <div className="overline">Open a free account</div>
            </div>
            <h2 className="font-display text-2xl text-[var(--emerald)] mt-3">New here?</h2>
            <div className="mt-5 space-y-4">
              <div>
                <div className="overline mb-1">Name</div>
                <input
                  className="lux-input"
                  required
                  value={register.name}
                  onChange={(e) => setRegister({ ...register, name: e.target.value })}
                  data-testid="guest-register-name"
                />
              </div>
              <div>
                <div className="overline mb-1">Email</div>
                <input
                  className="lux-input"
                  type="email"
                  required
                  value={register.email}
                  onChange={(e) => setRegister({ ...register, email: e.target.value })}
                  data-testid="guest-register-email"
                />
              </div>
              <div>
                <div className="overline mb-1">Password</div>
                <input
                  className="lux-input"
                  type="password"
                  required
                  minLength={6}
                  value={register.password}
                  onChange={(e) => setRegister({ ...register, password: e.target.value })}
                  data-testid="guest-register-password"
                />
              </div>
              <button
                type="submit"
                disabled={busyReg}
                className="btn-gold w-full"
                data-testid="guest-register-submit"
              >
                {busyReg ? "Creating…" : "Create free account"}
              </button>
            </div>
          </form>
        </div>

        <p className="text-xs text-[var(--muted)] mt-8 text-center">
          Hosts, your entrance is{" "}
          <Link to="/host-signin" className="text-[var(--gold)] hover:underline">
            here
          </Link>
          .
        </p>

        <div className="mt-10 pt-8 border-t border-[var(--emerald)]/15 text-center">
          <div className="overline justify-center inline-flex items-center gap-2">
            <Users size={14} strokeWidth={1.5} className="text-[var(--gold)]" />
            Travelling with friends?
          </div>
          <p className="text-sm text-[var(--ink-2)] mt-3 max-w-md mx-auto leading-snug">
            Plan one trip and invite your companions to view the itinerary —
            we'll send each one a personalised welcome email.
          </p>
          <button
            type="button"
            onClick={() => navigate("/groups/new")}
            className="mt-5 text-xs uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] inline-flex items-center gap-2 border border-[var(--brand-red)]/40 hover:border-[var(--emerald)] px-5 py-3"
            data-testid="guest-start-group-btn"
          >
            <Users size={14} strokeWidth={1.5} /> Start a group trip
          </button>
        </div>
      </div>
    </div>
  );
}
